<script setup lang="ts">
import type { SelectOption, SupportedLocale } from '@camelot/components'
import type { PrebookingDate } from '~/composables/prebooking-date'
import type { TranslationKey } from '~/composables/translate/translations'
import { CtCallout, CtDatePicker, CtSelect } from '@camelot/components'
import { useVModel } from '@vueuse/core'

// this is needed otherwise TSC complains that the private field _date is missing
type ModelPrebookingDate = Omit<PrebookingDate, '_date' | '_time'>

const props = defineProps<{
  modelValue: ModelPrebookingDate
  minDate?: Date
  maxDate?: Date
  dateLabelTranslationKey: TranslationKey
  errorMessage?: string
  hasGlobalError: boolean
}>()
const emits = defineEmits<{ 'update:modelValue': [value: PrebookingDate] }>()
const value = useVModel(props, 'modelValue', emits)

// Generate time options in the format HH:MM
const hourSelection = computed(() => {
  const options: SelectOption[] = []
  for (let hours = 0; hours < 24; hours++) {
    const hourString = String(hours).padStart(2, '0')
    options.push({
      label: `${hourString}:00`,
      value: `${hourString}:00`,
    })

    options.push({
      label: `${hourString}:30`,
      value: `${hourString}:30`,
    })
  }
  return options
})

// Convert between string time format and object time format
const selectedTime = computed({
  get: () => {
    if (!value.value.time)
      return undefined
    const { hours, minutes } = value.value.time
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  },
  set: (timeString: string | undefined) => {
    if (!timeString) {
      value.value.time = undefined
      return
    }

    const [hours, minutes] = timeString.split(':').map(Number)
    value.value.time = { hours, minutes }
  },
})

// Get locale based on current language
function getDateLocale(): SupportedLocale {
  switch (useTranslations().language.value) {
    case 'da': return 'da-DK'
    case 'de': return 'de-DE'
    case 'en': return 'en-US'
    case 'es': return 'es-ES'
    case 'fr': return 'fr-FR'
    case 'it': return 'it-IT'
    case 'nl': return 'nl-NL'
    default: return 'en-US'
  }
}

const hasError = computed(() => props.errorMessage || props.hasGlobalError)
</script>

<template>
  <div class="basis-1/2 flex flex-col gap-8">
    <label class="text-xs font-bold text-black-60" for="start">{{ translate(dateLabelTranslationKey) }}</label>
    <form class="flex flex-row gap-4">
      <CtDatePicker
        v-model="value.date"
        :format="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
        :class="{ '--error': hasError }"
        class="shrink-0 basis-4/6 md:basis-7/12 lg:max-xl:basis-6/12"
        :min-date="minDate"
        :max-date="maxDate"
        :locale="getDateLocale()"
        prefix-icon="calendar"
      />
      <CtSelect
        v-model="selectedTime"
        :options="hourSelection"
        :class="{ '--error': hasError }"
        placeholder="Select time"
      />
    </form>
    <CtCallout v-if="errorMessage" variant="error">
      {{ errorMessage }}
    </CtCallout>
  </div>
</template>
